import React, { useState, useEffect } from 'react';
import {
  Box,
  Paper,
  Typography,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  MenuItem,
  Tooltip,
  Avatar,
  AvatarGroup,
  Chip,
  FormControl,
  InputLabel,
  Select,
  DialogContentText,
} from '@mui/material';
import {
  format,
  startOfMonth,
  endOfMonth,
  eachDayOfInterval,
  isSameMonth,
  isSameDay,
  addMonths,
  subMonths,
  startOfWeek,
  endOfWeek,
  isToday,
  addDays,
} from 'date-fns';
import {
  ChevronLeft as ChevronLeftIcon,
  ChevronRight as ChevronRightIcon,
  AccessTime as AccessTimeIcon,
  Delete as DeleteIcon,
  Edit as EditIcon,
  Add as AddIcon,
} from '@mui/icons-material';
import { useAuth } from '../contexts/AuthContext';
import config from '../config';

const timeOffTypes = [
  { value: 'FULL_DAY', label: 'Full Day' },
  { value: 'HALF_DAY_AM', label: 'Morning (AM)' },
  { value: 'HALF_DAY_PM', label: 'Afternoon (PM)' },
];

function normalizeDate(date) {
  console.log('normalizeDate input:', date);
  // If it's a string date (from form input), parse it directly
  const d = typeof date === 'string' ? new Date(date + 'T12:00:00Z') : date;
  console.log('normalizeDate after new Date:', d);
  
  // Always use UTC methods to avoid timezone shifts
  const utcDate = new Date(Date.UTC(
    d.getUTCFullYear(),
    d.getUTCMonth(),
    d.getUTCDate(),
    12, 0, 0
  ));
  const result = utcDate.toISOString().split('T')[0];
  console.log('normalizeDate output:', result);
  return result;
}

function isSameDaySimple(date1, date2) {
  // Convert both dates to Date objects if they're not already
  const d1 = new Date(date1);
  const d2 = new Date(date2);
  
  console.log('Comparing dates:', {
    date1: d1.toISOString(),
    date2: d2.toISOString(),
    year1: d1.getUTCFullYear(),
    year2: d2.getUTCFullYear(),
    month1: d1.getUTCMonth(),
    month2: d2.getUTCMonth(),
    day1: d1.getUTCDate(),
    day2: d2.getUTCDate()
  });

  return d1.getUTCFullYear() === d2.getUTCFullYear() &&
         d1.getUTCMonth() === d2.getUTCMonth() &&
         d1.getUTCDate() === d2.getUTCDate();
}

const calendarStyles = {
  calendar: {
    width: '100%',
    backgroundColor: '#fff',
    borderRadius: '8px',
    overflow: 'hidden',
  },
  header: {
    display: 'grid',
    gridTemplateColumns: 'repeat(7, 1fr)',
    padding: { xs: '4px', sm: '8px' },
    textAlign: 'center',
    backgroundColor: '#f5f5f5',
    borderTopLeftRadius: '8px',
    borderTopRightRadius: '8px',
  },
  body: {
    display: 'grid',
    gridTemplateRows: 'repeat(6, 1fr)',
  },
  row: {
    display: 'grid',
    gridTemplateColumns: 'repeat(7, 1fr)',
    gap: '1px',
    backgroundColor: '#ddd',
    '&:last-child': {
      borderBottomLeftRadius: '8px',
      borderBottomRightRadius: '8px',
    },
  },
  cell: {
    minHeight: { xs: '60px', sm: '80px', md: '100px' },
    padding: { xs: '4px', sm: '8px' },
    backgroundColor: '#fff',
    '&.disabled': {
      opacity: 0.5,
    },
    '&.selected': {
      backgroundColor: '#f0f7ff',
    },
    overflow: 'hidden',
  },
  number: {
    display: 'block',
    marginBottom: { xs: '4px', sm: '8px' },
    fontWeight: 'bold',
    fontSize: { xs: '0.75rem', sm: '0.875rem', md: '1rem' },
  },
};

function TimeOffApp() {
  const [currentDate, setCurrentDate] = useState(new Date());
  const [timeOffRequests, setTimeOffRequests] = useState([]);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [selectedDate, setSelectedDate] = useState(new Date());
  const [selectedRequest, setSelectedRequest] = useState(null);
  const [formData, setFormData] = useState({
    date: '',
    type: 'FULL_DAY',
    notes: '',
    userId: ''
  });
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [requestToDelete, setRequestToDelete] = useState(null);
  const [allUsers, setAllUsers] = useState([]);
  const { token, user } = useAuth();

  useEffect(() => {
    console.log('Current user:', user);
    console.log('Current token:', token);
    fetchTimeOff();
    
    if (user && user.isAdmin) {
      fetchAllUsers();
    }
  }, [token, user]);

  const fetchTimeOff = async () => {
    try {
      const response = await fetch(`${config.apiUrl}/timeoff/team`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      const data = await response.json();
      const validRequests = data.filter(request => request && request.requestedBy);
      console.log('Fetched time off requests:', validRequests);
      
      // Log details about each request to debug half-day and reason fields
      validRequests.forEach((request, index) => {
        console.log(`Request ${index}:`, {
          id: request._id,
          date: request.startDate,
          user: `${request.requestedBy?.firstName} ${request.requestedBy?.lastName}`,
          halfDay: request.halfDay,
          reason: request.reason,
          fullRequest: JSON.stringify(request)
        });
      });
      
      setTimeOffRequests(validRequests);
    } catch (error) {
      console.error('Error fetching time off:', error);
      setTimeOffRequests([]);
    }
  };

  const fetchAllUsers = async () => {
    try {
      const response = await fetch(`${config.apiUrl}/admin/users`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      
      if (!response.ok) {
        throw new Error('Failed to fetch users');
      }
      
      const data = await response.json();
      console.log('Fetched all users:', data);
      setAllUsers(data);
    } catch (error) {
      console.error('Error fetching users:', error);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    try {
      if (!selectedDate) {
        throw new Error('Please select a date');
      }
      
      if (!user || !user._id) {
        throw new Error('User not authenticated');
      }

      // Format the date to YYYY-MM-DD
      const formattedDate = new Date(selectedDate).toISOString().split('T')[0];
      
      // Determine if it's a half day
      const isHalfDay = formData.type === 'HALF_DAY';
      
      // Determine which user ID to use
      // If admin and a user is selected, use that user's ID
      // Otherwise use the current user's ID
      const requestedById = (user.isAdmin && formData.userId) 
        ? formData.userId 
        : user._id;
      
      console.log('Submit date:', {
        input: selectedDate,
        formatted: formattedDate,
        userId: requestedById,
        currentUserId: user._id,
        isAdmin: user.isAdmin,
        selectedUserId: formData.userId,
        type: formData.type,
        isHalfDay: isHalfDay,
        notes: formData.notes,
        requestId: selectedRequest?._id
      });

      const url = selectedRequest 
        ? `${config.apiUrl}/timeoff/${selectedRequest._id}` 
        : `${config.apiUrl}/timeoff`;
      
      console.log('Request URL:', url);
      
      const requestBody = {
        startDate: formattedDate,
        endDate: formattedDate,
        requestedBy: requestedById,
        halfDay: isHalfDay,
        reason: formData.notes
      };
      
      const response = await fetch(url, {
        method: selectedRequest ? 'PUT' : 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify(requestBody)
      });

      console.log('Request payload:', requestBody);

      // Get the raw response text first
      const responseText = await response.text();
      console.log('Raw response:', responseText);
      
      // Try to parse as JSON
      let data;
      try {
        data = JSON.parse(responseText);
        console.log('Parsed response data:', data);
        console.log('Half day in response:', data.halfDay);
      } catch (parseError) {
        console.error('Failed to parse response as JSON:', parseError);
        throw new Error(`Server returned invalid response: ${responseText.substring(0, 100)}...`);
      }

      if (!response.ok) {
        console.error('Server error:', data);
        if (data.error && data.error.includes('duplicate key')) {
          throw new Error('You already have a time off request for this date');
        }
        throw new Error(data.error || 'Failed to submit request');
      }

      console.log('Success response:', data);
      await fetchTimeOff();
      setSelectedDate('');
      setDialogOpen(false);
    } catch (error) {
      console.error('Request error:', error);
      alert(error.message || 'Failed to submit request');
    }
  };

  const handleDelete = async (id) => {
    try {
      const response = await fetch(`${config.apiUrl}/timeoff/${id}`, {
        method: 'DELETE',
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (response.ok) {
        fetchTimeOff();
        handleCloseDialog();
      }
    } catch (error) {
      console.error('Error deleting time off:', error);
    }
  };

  const handleOpenDialog = (date, request = null) => {
    console.log('handleOpenDialog input date:', date);
    console.log('handleOpenDialog request:', request);
    
    if (!user) {
      console.log('No user loaded');
      return;
    }
    
    // Ensure we have a valid date object
    const validDate = date instanceof Date ? date : new Date(date);
    if (isNaN(validDate.getTime())) {
      console.error('Invalid date:', date);
      return;
    }
    
    setSelectedDate(validDate);
    setSelectedRequest(request);
    
    const normalizedDate = validDate.toISOString().split('T')[0];
    console.log('handleOpenDialog normalized date:', normalizedDate);
    
    // Check if the request has a halfDay property and set the type accordingly
    // Ensure halfDay is treated as a boolean
    const isHalfDay = request && request.halfDay === true;
    const type = isHalfDay ? 'HALF_DAY' : 'FULL_DAY';
    console.log('Setting form type:', type, 'based on halfDay:', isHalfDay, 'raw value:', request?.halfDay);
    
    setFormData({
      date: normalizedDate,
      type: type,
      notes: request && request.reason ? request.reason : '',
      userId: request && request.requestedBy?._id
    });
    
    setDialogOpen(true);
  };

  const handleCloseDialog = () => {
    setDialogOpen(false);
    setSelectedDate(null);
    setSelectedRequest(null);
    setFormData({ date: '', type: 'FULL_DAY', notes: '', userId: '' });
  };

  const handleDeleteClick = (e, request) => {
    e.stopPropagation();
    console.log('Delete clicked for request:', {
      id: request._id,
      startDate: request.startDate,
      requestedBy: request.requestedBy
    });
    setRequestToDelete(request);
    setDeleteDialogOpen(true);
  };

  const handleDeleteConfirm = async () => {
    if (requestToDelete && requestToDelete._id) {
      try {
        console.log('Sending delete request:', {
          id: requestToDelete._id,
          url: `${config.apiUrl}/timeoff/${requestToDelete._id}`
        });

        const response = await fetch(`${config.apiUrl}/timeoff/${requestToDelete._id}`, {
          method: 'DELETE',
          headers: { 
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json'
          }
        });

        const responseText = await response.text();
        console.log('Raw response:', responseText);

        let responseData;
        try {
          responseData = JSON.parse(responseText);
          console.log('Parsed response:', responseData);
        } catch (e) {
          console.error('Failed to parse response:', e);
          responseData = { error: 'Invalid server response' };
        }

        if (!response.ok) {
          console.error('Delete failed:', responseData);
          throw new Error(responseData.error || 'Failed to delete time off request');
        }

        console.log('Delete successful:', responseData);
        await fetchTimeOff();
        setDeleteDialogOpen(false);
        setRequestToDelete(null);
      } catch (error) {
        console.error('Delete error:', {
          error,
          message: error.message,
          requestToDelete,
          requestId: requestToDelete._id,
          userId: user._id
        });
        alert(error.message || 'Failed to delete time off request');
      }
    }
  };

  // Helper function to ensure valid date
  const ensureValidDate = (date) => {
    if (!date) return new Date();
    try {
      const d = new Date(date);
      return isNaN(d.getTime()) ? new Date() : d;
    } catch (error) {
      console.error('Invalid date:', date, error);
      return new Date();
    }
  };

  // Helper function to safely format date
  const safeFormat = (date, formatStr) => {
    try {
      const validDate = ensureValidDate(date);
      return format(validDate, formatStr);
    } catch (error) {
      console.error('Format error:', error);
      return '';
    }
  };

  const renderCalendar = () => {
    try {
      // Get all dates in the current month
      const validCurrentDate = ensureValidDate(currentDate);
      const monthStart = startOfMonth(validCurrentDate);
      const monthEnd = endOfMonth(validCurrentDate);
      const startDate = startOfWeek(monthStart);
      const endDate = endOfWeek(monthEnd);
      const dateFormat = "d";
      const rows = [];

      let days = [];
      let day = startDate;

      while (day <= endDate) {
        for (let i = 0; i < 7; i++) {
          const cloneDay = new Date(day);
          const timeOffForDay = timeOffRequests.filter(request => {
            try {
              const requestDate = new Date(request.startDate);
              return !isNaN(requestDate.getTime()) && isSameDay(requestDate, cloneDay);
            } catch (error) {
              console.error('Invalid date in request:', request);
              return false;
            }
          });

          days.push(
            <Box
              key={cloneDay.toISOString()}
              sx={{
                ...calendarStyles.cell,
                opacity: !isSameMonth(cloneDay, monthStart) ? 0.5 : 1,
                bgcolor: isSameDay(cloneDay, new Date()) ? '#f0f7ff' : '#fff',
                cursor: 'pointer',
                '&:hover': {
                  bgcolor: '#f5f5f5',
                },
              }}
              onClick={() => handleOpenDialog(cloneDay)}
            >
              <Typography sx={calendarStyles.number}>
                {safeFormat(cloneDay, dateFormat)}
              </Typography>
              {timeOffForDay.map((request) => {
                console.log('Rendering time off request:', {
                  id: request._id,
                  user: `${request.requestedBy?.firstName} ${request.requestedBy?.lastName}`,
                  halfDay: request.halfDay,
                  reason: request.reason,
                  fullRequest: JSON.stringify(request)
                });
                
                // Ensure halfDay is a boolean
                const isHalfDay = request.halfDay === true;
                
                return (
                <Tooltip
                  key={request._id}
                  title={`${request.requestedBy?.firstName} ${request.requestedBy?.lastName}${isHalfDay ? ' (Half Day)' : ''}${request.reason ? `: ${request.reason}` : ''}`}
                  arrow
                >
                  <Box
                    sx={{
                      bgcolor: isHalfDay 
                        ? 'transparent' // For half-day, we'll use gradient instead
                        : request.requestedBy?.color || '#1976d2',
                      color: 'white',
                      p: { xs: 0.25, sm: 0.5 },
                      borderRadius: 1,
                      fontSize: { xs: '0.65rem', sm: '0.75rem' },
                      mb: 0.5,
                      display: 'flex',
                      flexDirection: 'column', // Changed to column to accommodate the reason
                      ...(isHalfDay && {
                        background: `linear-gradient(to right, ${request.requestedBy?.color || '#1976d2'} 50%, rgba(255,255,255,0.7) 50%)`,
                        position: 'relative',
                        '&::before': {
                          content: '""',
                          position: 'absolute',
                          top: 0,
                          left: 0,
                          right: 0,
                          bottom: 0,
                          background: 'rgba(0,0,0,0.1)',
                          zIndex: 0,
                          pointerEvents: 'none'
                        }
                      })
                    }}
                    onClick={(e) => {
                      e.stopPropagation();
                      const requestDate = new Date(request.startDate);
                      if (!isNaN(requestDate.getTime())) {
                        handleOpenDialog(requestDate, request);
                      }
                    }}
                  >
                    <Box sx={{ 
                      display: 'flex', 
                      justifyContent: 'space-between',
                      alignItems: 'center',
                      width: '100%',
                      position: 'relative',
                      zIndex: 1
                    }}>
                      <Typography 
                        component="span" 
                        sx={{ 
                          fontSize: { xs: '0.65rem', sm: '0.75rem' },
                          whiteSpace: 'nowrap',
                          overflow: 'hidden',
                          textOverflow: 'ellipsis',
                          maxWidth: { xs: 'calc(100% - 20px)', sm: 'calc(100% - 24px)' }
                        }}
                      >
                        {/* Show only first name on xs screens, full name on larger screens */}
                        <Box sx={{ display: { xs: 'block', sm: 'none' } }}>
                          {request.requestedBy?.firstName}
                        </Box>
                        <Box sx={{ display: { xs: 'none', sm: 'block' } }}>
                          {request.requestedBy?.firstName} {request.requestedBy?.lastName}
                        </Box>
                      </Typography>
                      {/* Allow user to delete their own entries OR admins to delete any entry */}
                      {user && (user._id === request.requestedBy?._id || user.isAdmin) && (
                        <IconButton
                          size="small"
                          onClick={(e) => {
                            e.stopPropagation();
                            handleDeleteClick(e, request);
                          }}
                          sx={{ 
                            color: 'white', 
                            p: 0,
                            '& .MuiSvgIcon-root': {
                              fontSize: { xs: '0.75rem', sm: '1rem' }
                            }
                          }}
                        >
                          <DeleteIcon fontSize="inherit" />
                        </IconButton>
                      )}
                    </Box>
                    
                    {/* Note/Reason - only show on larger screens */}
                    {request.reason && (
                      <Typography 
                        variant="caption" 
                        sx={{ 
                          fontSize: { xs: '0.6rem', sm: '0.65rem' },
                          fontStyle: 'italic',
                          opacity: 0.9,
                          whiteSpace: 'nowrap',
                          overflow: 'hidden',
                          textOverflow: 'ellipsis',
                          position: 'relative',
                          zIndex: 1,
                          display: { xs: 'none', sm: 'block' }
                        }}
                      >
                        {request.reason}
                      </Typography>
                    )}
                  </Box>
                </Tooltip>
                );
              })}
            </Box>
          );
          day = addDays(day, 1);
        }
        rows.push(
          <Box sx={calendarStyles.row} key={day.toISOString()}>
            {days}
          </Box>
        );
        days = [];
      }
      return rows;
    } catch (error) {
      console.error('Error rendering calendar:', error);
      return null;
    }
  };

  return (
    <Box sx={{ p: { xs: 1, sm: 2, md: 3 } }}>
      <Box sx={{ 
        display: 'flex', 
        alignItems: 'center', 
        justifyContent: 'space-between',
        mb: 2,
        flexWrap: { xs: 'wrap', sm: 'nowrap' }
      }}>
        <Box sx={{ 
          display: 'flex', 
          alignItems: 'center',
          width: { xs: '100%', sm: 'auto' },
          justifyContent: { xs: 'center', sm: 'flex-start' },
          mb: { xs: 1, sm: 0 }
        }}>
          <IconButton onClick={() => setCurrentDate(subMonths(ensureValidDate(currentDate), 1))}>
            <ChevronLeftIcon />
          </IconButton>
          <Typography variant="h6" sx={{ mx: 2 }}>
            {safeFormat(ensureValidDate(currentDate), 'MMMM yyyy')}
          </Typography>
          <IconButton onClick={() => setCurrentDate(addMonths(ensureValidDate(currentDate), 1))}>
            <ChevronRightIcon />
          </IconButton>
        </Box>
        
        <Button
          variant="contained"
          color="primary"
          startIcon={<AddIcon />}
          size="small"
          sx={{ 
            width: { xs: '100%', sm: 'auto' },
            mb: { xs: 1, sm: 0 }
          }}
          onClick={() => {
            const today = new Date();
            setSelectedDate(today);
            setFormData({
              ...formData,
              date: normalizeDate(today)
            });
            setSelectedRequest(null);
            setDialogOpen(true);
          }}
        >
          Request Time Off
        </Button>
      </Box>

      <Paper sx={calendarStyles.calendar}>
        <Box sx={calendarStyles.header}>
          {['S', 'M', 'T', 'W', 'T', 'F', 'S'].map((day, index) => (
            <Typography 
              key={index} 
              variant="subtitle2" 
              sx={{ 
                fontSize: { xs: '0.7rem', sm: '0.8rem', md: '0.875rem' },
                display: { xs: 'block', sm: 'block' }
              }}
            >
              <Box sx={{ display: { xs: 'block', sm: 'none' } }}>{day}</Box>
              <Box sx={{ display: { xs: 'none', sm: 'block' } }}>
                {['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'][index]}
              </Box>
            </Typography>
          ))}
        </Box>
        <Box sx={calendarStyles.body}>
          {renderCalendar()}
        </Box>
      </Paper>

      <Dialog 
        open={dialogOpen} 
        onClose={handleCloseDialog}
        aria-labelledby="form-dialog-title"
      >
        <DialogTitle id="form-dialog-title">
          {selectedRequest 
            ? `Edit Time Off Request for ${format(new Date(selectedRequest.startDate), 'MMMM d, yyyy')}` 
            : 'Request New Time Off'}
        </DialogTitle>
        <DialogContent>
          <TextField
            autoFocus
            margin="dense"
            label="Date"
            type="date"
            fullWidth
            value={formData.date}
            onChange={(e) => setFormData({ ...formData, date: e.target.value })}
            InputLabelProps={{ shrink: true }}
          />
          
          {/* User selection dropdown for admins */}
          {user && user.isAdmin && (
            <FormControl fullWidth margin="dense">
              <InputLabel>User</InputLabel>
              <Select
                value={formData.userId || user._id}
                onChange={(e) => {
                  console.log('User changed to:', e.target.value);
                  setFormData({ ...formData, userId: e.target.value });
                }}
                label="User"
              >
                <MenuItem value={user._id}>
                  {user.firstName} {user.lastName} (You)
                </MenuItem>
                {allUsers
                  .filter(u => u._id !== user._id) // Filter out current user
                  .map(u => (
                    <MenuItem key={u._id} value={u._id}>
                      {u.firstName} {u.lastName}
                    </MenuItem>
                  ))
                }
              </Select>
            </FormControl>
          )}
          
          <FormControl fullWidth margin="dense">
            <InputLabel>Type</InputLabel>
            <Select
              value={formData.type}
              onChange={(e) => {
                console.log('Type changed to:', e.target.value);
                setFormData({ ...formData, type: e.target.value });
              }}
              label="Type"
            >
              <MenuItem value="FULL_DAY">Full Day Off</MenuItem>
              <MenuItem value="HALF_DAY">Half Day Off</MenuItem>
            </Select>
          </FormControl>
          <TextField
            margin="dense"
            label="Reason / Notes (optional)"
            type="text"
            fullWidth
            multiline
            rows={4}
            value={formData.notes}
            onChange={(e) => setFormData({ ...formData, notes: e.target.value })}
            placeholder="Enter reason for time off or any notes"
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseDialog} color="primary">
            Cancel
          </Button>
          <Button onClick={handleSubmit} color="primary" variant="contained">
            {selectedRequest ? 'Update Request' : 'Submit Request'}
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog
        open={deleteDialogOpen}
        onClose={() => setDeleteDialogOpen(false)}
      >
        <DialogTitle>Confirm Delete</DialogTitle>
        <DialogContent>
          <DialogContentText>
            Are you sure you want to delete this time off request for{' '}
            {requestToDelete && safeFormat(new Date(requestToDelete.startDate), 'MMMM d, yyyy')}?
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDeleteDialogOpen(false)}>Cancel</Button>
          <Button onClick={handleDeleteConfirm} color="error" variant="contained">
            Delete
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}

export default TimeOffApp; 